// frontend/src/pages/FranchisingForm.jsx
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import TornaAlMenu from "../components/TornaAlMenu";

const API_URL = process.env.REACT_APP_API_URL || "";

// helper per evitare value=null sugli input controllati
const str = (x) => (x ?? "").toString();

const initial = {
  code: "",
  name: "",
  referent: "",
  address: "",

  // fatturazione
  fatt_ragione_sociale: "",
  fatt_piva: "",
  fatt_cf: "",
  fatt_indirizzo: "",
  fatt_cap: "",
  fatt_citta: "",
  fatt_provincia: "",
  fatt_nazione: "",
  fatt_email: "",
  fatt_pec: "",
  fatt_sdi: "",

  // amministrazione
  telefono: "",
  iban: "",
  banca: "",
  note_admin: "",

  // account (solo create)
  user_username: "",
  user_password: "",
  user_email: "",
};

export default function FranchisingForm() {
  const nav = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [data, setData] = useState(initial);
  const [loading, setLoading] = useState(true); // carica ruolo e (se serve) dati form
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  // 🔐 guard + load dati (edit)
  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem("access");
        const meR = await fetch(`${API_URL}/api/me/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const me = await meR.json().catch(() => ({}));
        const role = (me?.role || "").toUpperCase();

        if (role !== "MADRE") {
          alert("Accesso negato: solo l’utente SEDE può creare o modificare un franchising.");
          nav("/franchising", { replace: true });
          return;
        }

        if (isEdit) {
          const r = await fetch(`${API_URL}/api/franchising/${id}/`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const j = await r.json().catch(() => ({}));
          if (!r.ok) throw new Error(j?.detail || "Errore caricamento franchising");

          // normalizza a stringhe dove serve, preservando solo le chiavi che usiamo
          const norm = { ...initial };
          Object.keys(norm).forEach((k) => {
            norm[k] = str(j[k] !== undefined ? j[k] : norm[k]);
          });
          // alcuni backend usano 'phone'
          if (!norm.telefono && j.phone) norm.telefono = str(j.phone);

          // 'code' in edit potrebbe essere readonly ma lo mostriamo comunque
          norm.code = str(j.code);
          setData(norm);
        }
      } catch (e) {
        setErr(e.message || "Errore");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isEdit, nav]);

  const onChange = (e) =>
    setData((d) => ({ ...d, [e.target.name]: e.target.value }));

    const validate = () => {
      if (!data.name?.trim()) return "Il nome commerciale è obbligatorio.";
      if (!isEdit) {
        if (!data.user_username?.trim() || !data.user_password?.trim()) {
          return "Username e Password sono obbligatori per creare il franchising.";
        }
      }
      if ((data.user_username?.trim() || data.user_password?.trim()) && !(data.user_username?.trim() && data.user_password?.trim())) {
        return "Per impostare le credenziali servono sia username che password.";
      }
      return "";
    };

    const buildPayload = () => {
      return {
        code: data.code || undefined,
        name: data.name,
        referent: data.referent || "",
        address: data.address || "",
        fatt_ragione_sociale: data.fatt_ragione_sociale || "",
        fatt_piva: data.fatt_piva || "",
        fatt_cf: data.fatt_cf || "",
        fatt_indirizzo: data.fatt_indirizzo || "",
        fatt_cap: data.fatt_cap || "",
        fatt_citta: data.fatt_citta || "",
        fatt_provincia: data.fatt_provincia || "",
        fatt_nazione: data.fatt_nazione || "",
        fatt_email: data.fatt_email || "",
        fatt_pec: data.fatt_pec || "",
        fatt_sdi: data.fatt_sdi || "",
        telefono: data.telefono || "",
        iban: data.iban || "",
        banca: data.banca || "",
        note_admin: data.note_admin || "",
      };
    };

  const onSubmit = async (e) => {
    e.preventDefault();

    const v = validate();
    if (v) {
      setErr(v);
      return;
    }

    try {
      setSaving(true);
      setErr("");
      const token = localStorage.getItem("access");
      const method = isEdit ? "PATCH" : "POST";
      const url = isEdit
        ? `${API_URL}/api/franchising/${id}/`
        : `${API_URL}/api/franchising/`;
      const body = buildPayload();

      const r = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });
      const text = await r.text();
      const j = text ? JSON.parse(text) : {};
      if (!r.ok) {
        const msg =
          j?.detail ||
          (j && typeof j === "object" ? Object.values(j).flat().join(" ") : "") ||
          "Errore salvataggio franchising";
        throw new Error(msg);
      }
        const frId = isEdit ? id : (j.id ?? j.pk);
        if ((data.user_username || "").trim() || (data.user_password || "").trim() || (data.user_email || "").trim()) {
          if (!frId) throw new Error("Franchising salvato, ma ID non trovato per impostare le credenziali.");

          const credR = await fetch(`${API_URL}/api/franchising/${frId}/credenziali/`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              username: data.user_username.trim(),
              password: data.user_password,
              email: data.user_email?.trim() || undefined,
            }),
          });
          const credText = await credR.text();
          const credJ = credText ? JSON.parse(credText) : {};
          if (!credR.ok) {
            throw new Error(credJ?.detail || "Franchising salvato, ma errore impostazione credenziali.");
          }
        }

        nav("/franchising");
    } catch (e) {
      setErr(e.message || "Errore");
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async () => {
    if (!isEdit) return;
    if (!window.confirm("Sei sicuro di voler eliminare questo franchising?")) return;
    try {
      const token = localStorage.getItem("access");
      const r = await fetch(`${API_URL}/api/franchising/${id}/`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok && r.status !== 204) {
        const t = await r.text().catch(() => "");
        throw new Error(t || "Impossibile eliminare il franchising.");
      }
      nav("/franchising");
    } catch (e) {
      setErr(e.message || "Errore eliminazione franchising.");
    }
  };

  if (loading)
    return (
      <div className="container mt-4">
        <TornaAlMenu to="/franchising" />
        <p className="text-muted">Caricamento…</p>
      </div>
    );

  return (
    <div className="container mt-4">
      <TornaAlMenu to="/franchising" />
      <h2 className="mb-3">
        {isEdit ? "✏️ Modifica Franchising" : "➕ Nuovo Franchising"}
      </h2>
      {err && <div className="alert alert-danger">{err}</div>}

      <form onSubmit={onSubmit} className="card shadow-sm">
        <div className="card-body">
          {/* Dati generali */}
          <h5 className="mb-3">Dati generali</h5>
          <div className="row g-3">
            <div className="col-md-3">
              <label className="form-label">Codice (facoltativo)</label>
              <input
                name="code"
                className="form-control"
                placeholder="es. FR0012 (vuoto = auto)"
                value={str(data.code)}
                onChange={onChange}
                disabled={isEdit}
              />
              <div className="form-text">
                Lascia vuoto per assegnazione automatica.
              </div>
            </div>
            <div className="col-md-5">
              <label className="form-label">Nome commerciale *</label>
              <input
                name="name"
                className="form-control"
                value={str(data.name)}
                onChange={onChange}
                required
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">Referente</label>
              <input
                name="referent"
                className="form-control"
                value={str(data.referent)}
                onChange={onChange}
              />
            </div>
            <div className="col-12">
              <label className="form-label">Indirizzo (sede operativa)</label>
              <input
                name="address"
                className="form-control"
                value={str(data.address)}
                onChange={onChange}
              />
            </div>
          </div>

          <hr className="my-4" />

          {/* Fatturazione */}
          <h5 className="mb-3">Dati di fatturazione</h5>
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label">Ragione sociale</label>
              <input
                name="fatt_ragione_sociale"
                className="form-control"
                value={str(data.fatt_ragione_sociale)}
                onChange={onChange}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Partita IVA</label>
              <input
                name="fatt_piva"
                className="form-control"
                value={str(data.fatt_piva)}
                onChange={onChange}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Codice Fiscale</label>
              <input
                name="fatt_cf"
                className="form-control"
                value={str(data.fatt_cf)}
                onChange={onChange}
              />
            </div>

            <div className="col-md-6">
              <label className="form-label">Indirizzo</label>
              <input
                name="fatt_indirizzo"
                className="form-control"
                value={str(data.fatt_indirizzo)}
                onChange={onChange}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label">CAP</label>
              <input
                name="fatt_cap"
                className="form-control"
                value={str(data.fatt_cap)}
                onChange={onChange}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label">Città</label>
              <input
                name="fatt_citta"
                className="form-control"
                value={str(data.fatt_citta)}
                onChange={onChange}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label">Provincia</label>
              <input
                name="fatt_provincia"
                className="form-control"
                value={str(data.fatt_provincia)}
                onChange={onChange}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Nazione</label>
              <input
                name="fatt_nazione"
                className="form-control"
                value={str(data.fatt_nazione)}
                onChange={onChange}
              />
            </div>

            <div className="col-md-3">
              <label className="form-label">Email fatturazione</label>
              <input
                type="email"
                name="fatt_email"
                className="form-control"
                value={str(data.fatt_email)}
                onChange={onChange}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">PEC</label>
              <input
                type="email"
                name="fatt_pec"
                className="form-control"
                value={str(data.fatt_pec)}
                onChange={onChange}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Codice SDI</label>
              <input
                name="fatt_sdi"
                className="form-control"
                value={str(data.fatt_sdi)}
                onChange={onChange}
              />
            </div>
          </div>

          <hr className="my-4" />

          {/* Amministrazione / Pagamenti */}
          <h5 className="mb-3">Amministrazione & Pagamenti</h5>
          <div className="row g-3">
            <div className="col-md-3">
              <label className="form-label">Telefono</label>
              <input
                name="telefono"
                className="form-control"
                value={str(data.telefono)}
                onChange={onChange}
              />
            </div>
            <div className="col-md-5">
              <label className="form-label">IBAN</label>
              <input
                name="iban"
                className="form-control"
                value={str(data.iban)}
                onChange={onChange}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">Banca</label>
              <input
                name="banca"
                className="form-control"
                value={str(data.banca)}
                onChange={onChange}
              />
            </div>
            <div className="col-12">
              <label className="form-label">Note amministrative</label>
              <textarea
                name="note_admin"
                className="form-control"
                rows={3}
                value={str(data.note_admin)}
                onChange={onChange}
              />
            </div>
          </div>

            <>
              <hr className="my-4" />
              {/* Account di accesso */}
              <h5 className="mb-3">{isEdit ? "Credenziali di accesso" : "Account di accesso (obbligatorio)"}</h5>
              <div className="row g-3">
                <div className="col-md-4">
                  <label className="form-label">Username {isEdit ? "" : "*"}</label>
                  <input
                    name="user_username"
                    className="form-control"
                    value={str(data.user_username)}
                    onChange={onChange}
                    required={!isEdit}
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Password {isEdit ? "" : "*"}</label>
                  <input
                    type="password"
                    name="user_password"
                    className="form-control"
                    value={str(data.user_password)}
                    onChange={onChange}
                    required={!isEdit}
                    autoComplete="new-password"
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    name="user_email"
                    className="form-control"
                    value={str(data.user_email)}
                    onChange={onChange}
                  />
                </div>
              </div>
              <div className="form-text">
                {isEdit ? "Compila username e password per aggiornare le credenziali del franchising." : "L’account sarà creato e collegato al franchising."}
              </div>
            </>
        </div>

        <div className="card-footer d-flex justify-content-between">
          <div className="d-flex gap-2">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => nav("/franchising")}
            >
              Annulla
            </button>
            {isEdit && (
              <button
                type="button"
                className="btn btn-outline-danger"
                onClick={onDelete}
              >
                🗑️ Elimina
              </button>
            )}
          </div>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? "Salvataggio…" : isEdit ? "Salva modifiche" : "Crea franchising"}
          </button>
        </div>
      </form>
    </div>
  );
}
